// SPDX-License-Identifier: GPL-3.0-or-later
// The look of the parts of the table: one token colour and one glow level for each part
// (THEME.md section 2, "Colour ruling"). The table data keeps its grays. The render layer reads
// these rules by the name of the part, so the table data does not change.
// Every hex here comes from palette.ts. A colour is never the only cue: each part has a shape,
// and the lamps and the targets have labels.

import { BANK_HEX, FAMILY_HEX, TOKEN_HEX } from "./palette.ts";

/** The look of one part. */
export interface PartLook {
  /** A palette hex. */
  readonly hex: number;
  /** Emissive intensity of the part. 0 is dark, 1 is a bright neon tube. */
  readonly glow: number;
  /** True: the base colour of the part is the hex too (a face), not only the glow. */
  readonly paint: boolean;
}

function look(hex: number, glow: number, paint = false): PartLook {
  return { hex, glow, paint };
}

/** Rules for the walls. The first match wins. A wall that no rule names stays gray. */
const WALL_RULES: readonly (readonly [RegExp, PartLook])[] = [
  [/^Sling/, look(FAMILY_HEX.sling, 1.1, true)],
  [/^OrbitRail/, look(FAMILY_HEX.orbit, 1)],
  [/^(Inlane|Inlet)Guide/, look(FAMILY_HEX.lane, 0.95)],
  [/^TopDivider/, look(FAMILY_HEX.lane, 0.85)],
  [/^Ramp\w*ExitPad/, look(FAMILY_HEX.ramp, 0.8)],
  [/^Apron/, look(TOKEN_HEX.block, 0.6)],
  [/^(ArchWall|LeftWall|RightWall|ShooterWall|PlungerBack)$/, look(TOKEN_HEX.force, 0.55)],
];

/** The look of a wall by name, or null for a wall that stays gray. */
export function wallLook(name: string): PartLook | null {
  for (const [pattern, found] of WALL_RULES) if (pattern.test(name)) return found;
  return null;
}

/** One neon colour for each wire ramp. A ramp that is not listed has the ramp family colour. */
const RAMP_HEX: Readonly<Record<string, number>> = {
  HallucinationRamp: FAMILY_HEX.ramp,
  MergeRamp: TOKEN_HEX.ask,
};

/** The look of the run rails of a ramp. The side guides use `guideLookOf`. */
export function rampLook(name: string): PartLook {
  const hex = Object.hasOwn(RAMP_HEX, name) ? (RAMP_HEX[name] as number) : FAMILY_HEX.ramp;
  return look(hex, 1.15);
}

/** The look of the side guides of a ramp: the same colour, a lower glow. */
export function guideLookOf(run: PartLook): PartLook {
  return look(run.hex, run.glow * 0.65);
}

/** The cap colour of each pop bumper, in the order of the table. No two neighbours are equal. */
export const BUMPER_CAP_HEX: readonly number[] = [TOKEN_HEX.ask, TOKEN_HEX.force, TOKEN_HEX.allow];

/** The look of the cap of the bumper with this index. */
export function bumperLook(index: number): PartLook {
  const hex = BUMPER_CAP_HEX[Math.max(0, index) % BUMPER_CAP_HEX.length] as number;
  return look(hex, 0.95, true);
}

/** The look of the face of a target, or null when the name is not in a bank. */
export function targetLook(name: string): PartLook | null {
  if (name.startsWith("TokenTarget")) return look(BANK_HEX.token, 0.7, true);
  if (name.startsWith("Deny")) return look(BANK_HEX.deny, 0.7, true);
  return null;
}

/** The look of a rubber post. */
export function rubberLook(): PartLook {
  return look(FAMILY_HEX.orbit, 0.95, true);
}

/** The look of the plate of the spinner. */
export function spinnerLook(): PartLook {
  return look(FAMILY_HEX.spinner, 1, true);
}

/** The look of the rim of a kicker. */
export function kickerLook(name: string): PartLook {
  if (name === "Saucer") return look(TOKEN_HEX.force, 0.9);
  if (name === "Drain") return look(TOKEN_HEX.block, 0.6);
  return look(FAMILY_HEX.kicker, 0.9);
}

/** The look of the edge of a flipper bat. The body stays white. */
export function flipperEdgeLook(name: string): PartLook {
  if (name === "LeftFlipper") return look(TOKEN_HEX.force, 1, true);
  if (name === "RightFlipper") return look(TOKEN_HEX.ask, 1, true);
  return look(TOKEN_HEX.allow, 1, true);
}

/** The look of a lane trigger or a shot trigger, or null for a trigger that stays gray. */
export function triggerLook(name: string): PartLook | null {
  if (/^(Lane|TopLane)/.test(name)) return look(FAMILY_HEX.lane, 0.7, true);
  if (/^Orbit/.test(name)) return look(FAMILY_HEX.orbit, 0.7, true);
  if (/^Ramp/.test(name)) return look(FAMILY_HEX.ramp, 0.7, true);
  return null;
}

/** The look of an orbit gate or a lane gate. */
export function gateLook(name: string): PartLook {
  return name.startsWith("Orbit") ? look(FAMILY_HEX.orbit, 0.8) : look(FAMILY_HEX.lane, 0.8);
}
